/**
 * PORTED — RO-3-03 / #44296.
 * Origin: `excellent/app/favorites/client.tsx`.
 *
 * Changes: next/link → RouterLink. Data plane already used favorites contract.
 */

import { useState } from "react";
import { useSWRConfig } from "swr";
import { Star, X } from "@excellent/ui/icons";
import { Card, EmptyState, cn, formatRelativeTime } from "@excellent/ui";
import { RouterLink as Link } from "@excellent/ui/router";
import { apiCall, apiKey, useApi } from "@excellent/ui/api";
import { favorites as favoritesContract } from "@excellent/ui/api/contracts/favorites";

export function FavoritesListClient() {
  const { data, isLoading } = useApi(favoritesContract.list, {});
  const favorites = data?.favorites ?? [];
  const [pending, setPending] = useState<string | null>(null);
  const { mutate } = useSWRConfig();

  async function remove(href: string, label: string) {
    if (pending) return;
    setPending(href);
    try {
      await apiCall(favoritesContract.toggle, { body: { href, label } });
      await mutate(apiKey(favoritesContract.list, {}));
    } finally {
      setPending(null);
    }
  }

  if (!isLoading && favorites.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Star strokeWidth={1.5} className="h-5 w-5" />}
          title="No favorites yet"
          description="Star any page from the top nav to pin it here and in the left rail."
        />
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden p-0">
      <ul className="divide-y divide-border/40">
        {favorites.map((f) => (
          <li key={f.href} className="group flex items-center gap-3 px-4 py-2.5">
            <Star
              strokeWidth={1.5}
              className="h-3.5 w-3.5 shrink-0 fill-current text-foreground"
            />
            <div className="min-w-0 flex-1">
              <Link
                href={f.href}
                className="block truncate type-body text-foreground hover:underline"
              >
                {f.label}
              </Link>
              <p className="truncate type-subtext text-muted-foreground">{f.href}</p>
            </div>
            <span className="type-subtext text-muted-foreground whitespace-nowrap">
              {formatRelativeTime(f.created_at)}
            </span>
            <button
              type="button"
              onClick={() => void remove(f.href, f.label)}
              disabled={pending === f.href}
              aria-label={`Remove ${f.label} from favorites`}
              className={cn(
                "rounded-md p-1 text-muted-foreground/60 transition-colors",
                "hover:text-foreground hover:bg-secondary/60 disabled:opacity-40",
              )}
            >
              <X strokeWidth={1.75} className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
